import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Form,
  Table,
  Card,
  Row,
  Col,
  Modal,
  ListGroup,
  Spinner,
} from 'react-bootstrap';
import AgreementNameField from '../components/AgreementNameField';
import OperationProgress from '../components/OperationProgress';
import { clearBatchProgress, initBatchProgress, tickBatchProgress } from '../utils/batchProgress';
import { hireRateFieldGroupClass } from '../constants/hireRateFields';
import { cleanupModalBody, scheduleModalCleanup } from '../utils/modalCleanup';
import './Overview.css';

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  onClick,
  className,
  'aria-label': ariaLabel,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      className={`form-check-input ${className || ''}`.trim()}
      checked={checked}
      onChange={onChange}
      onClick={onClick}
      aria-label={ariaLabel}
    />
  );
}

const RATE_COLUMNS = [
  { field: 'week_rate', label: 'Week Rate', min: 0, step: '0.01' },
  { field: 'day_rate', label: 'Day Rate', min: 0, step: '0.01' },
  { field: 'month_rate', label: 'Month Rate', min: 0.01, step: '0.01' },
  { field: 'hour_rate', label: 'Hour Rate', min: 0, step: '0.01' },
  { field: 'one_day_rate', label: '1 Day Rate', min: 0.01, step: '0.01' },
  { field: 'two_day_rate', label: '2 Day Rate', min: 0.01, step: '0.01' },
  { field: 'three_day_rate', label: '3 Day Rate', min: 0.01, step: '0.01' },
  { field: 'four_day_rate', label: '4 Day Rate', min: 0.01, step: '0.01' },
  { field: 'part_day_rate', label: 'Part Day Rate', min: 0.01, step: '0.01' },
  { field: 'sc_rate', label: 'SC Rate', min: 0.01, step: '0.01' },
  { field: 'unit_rate', label: 'Unit Rate', min: 0.01, step: '0.01' },
  { field: 'switch_week_rate', label: 'Switch Week Rate', min: 0.01, step: '0.01' },
  { field: 'switch_after_weeks', label: 'Switch After Weeks', min: 1, step: '1', integer: true },
  {
    field: 'min_hire_period_days',
    label: 'Min Hire Period Days',
    min: 2,
    step: '1',
    integer: true,
  },
  { field: 'disc_pct', label: 'Disc Pct', min: 1, step: '0.01' },
  { field: 'o1_rate', label: 'O1 Rate', min: 0.01, step: '0.01' },
  { field: 'o2_rate', label: 'O2 Rate', min: 0.01, step: '0.01' },
  { field: 'o3_rate', label: 'O3 Rate', min: 0.01, step: '0.01' },
  { field: 'o4_rate', label: 'O4 Rate', min: 0.01, step: '0.01' },
];

function emptyForm() {
  return {
    name: '',
    cat: '',
    rate_name: '',
    special_instructions: '',
    ...Object.fromEntries(RATE_COLUMNS.map(({ field }) => [field, ''])),
  };
}

function toForm(record) {
  if (!record) return emptyForm();
  const data = {
    name: record.name ?? '',
    cat: record.cat ?? '',
    rate_name: record.rate_name ?? '',
    special_instructions: record.special_instructions ?? '',
  };
  for (const col of RATE_COLUMNS) {
    data[col.field] = record[col.field] == null ? '' : String(record[col.field]);
  }
  return data;
}

function parsePayload(formData) {
  const payload = {
    name: formData.name?.trim() || null,
    cat: formData.cat.trim(),
    rate_name: formData.rate_name || null,
    special_instructions: formData.special_instructions || null,
  };
  for (const col of RATE_COLUMNS) {
    const raw = formData[col.field];
    if (raw === '' || raw == null) {
      payload[col.field] = null;
    } else {
      payload[col.field] = col.integer ? parseInt(raw, 10) : parseFloat(raw);
    }
  }
  return payload;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function buildLookupMap(items, codeKey, nameKey) {
  const map = new Map();
  for (const item of asArray(items)) {
    const code = item?.[codeKey];
    if (code) {
      map.set(String(code).toLowerCase(), item[nameKey] || code);
    }
  }
  return map;
}

function LookupCell({ code, label }) {
  if (!code) {
    return '—';
  }

  if (!label || label === code) {
    return code;
  }

  return (
    <>
      <div>{label}</div>
      <div className="small text-muted">{code}</div>
    </>
  );
}

function itemSelectionKey(catCode, stockNo) {
  return `${catCode}|${stockNo}`;
}

function flattenHireTree(hireTree) {
  const categories = [];

  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (node.type === 'hire-category') {
        const items = (node.children || [])
          .filter((child) => child.type === 'hire-stock' || child.type === 'hire-stock-bulk')
          .map((child) => ({
            stockNo: child.code,
            name: child.name,
          }));
        categories.push({
          code: node.code,
          name: node.name,
          items,
        });
      } else if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(hireTree);
  return categories.sort((a, b) => a.name.localeCompare(b.name));
}

function emptyRateForm() {
  return {
    special_instructions: '',
    ...Object.fromEntries(RATE_COLUMNS.map(({ field }) => [field, ''])),
  };
}

function parseRateForm(formData) {
  const payload = {
    special_instructions: formData.special_instructions || null,
  };
  for (const col of RATE_COLUMNS) {
    const raw = formData[col.field];
    if (raw === '' || raw == null) {
      payload[col.field] = null;
    } else {
      payload[col.field] = col.integer ? parseInt(raw, 10) : parseFloat(raw);
    }
  }
  return payload;
}

function buildQuickCreatePayload(agreementName, catCode, stockNo, rateValues = {}) {
  return {
    name: agreementName?.trim() || null,
    cat: catCode,
    rate_name: stockNo,
    special_instructions: null,
    ...Object.fromEntries(RATE_COLUMNS.map(({ field }) => [field, null])),
    ...rateValues,
  };
}

function QuickCreateModal({ show, onClose, onCreated, busy = false }) {
  const [agreementName, setAgreementName] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState('');
  const [modalError, setModalError] = useState('');
  const [filter, setFilter] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [rateForm, setRateForm] = useState(emptyRateForm);
  const submittedRef = useRef(false);

  const handleModalExited = () => {
    if (!show) {
      scheduleModalCleanup();
    }
  };

  useEffect(() => {
    if (!show) {
      setShowRatesModal(false);
      submittedRef.current = false;
      return undefined;
    }

    submittedRef.current = false;
    setShowRatesModal(false);
    setRateForm(emptyRateForm());
    setAgreementName('');
    setFilter('');
    setSelectedKeys(new Set());
    setModalError('');
    setTreeError('');
    setExpandedCategories(new Set());

    let cancelled = false;

    const fetchTree = async () => {
      setLoadingTree(true);
      try {
        const response = await axios.get('/api/overview');
        if (!cancelled) {
          const flattened = flattenHireTree(response.data?.hireTree || []);
          setCategories(flattened);
          setExpandedCategories(new Set(flattened.map((cat) => cat.code)));
        }
      } catch (err) {
        if (!cancelled) {
          setTreeError(err.response?.data?.error || 'Failed to load hire items');
          setCategories([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTree(false);
        }
      }
    };

    fetchTree();
    return () => {
      cancelled = true;
    };
  }, [show]);

  const filteredCategories = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) {
      return categories;
    }

    return categories
      .map((cat) => {
        const catMatches =
          cat.code.toLowerCase().includes(query) || cat.name.toLowerCase().includes(query);
        const matchingItems = cat.items.filter((item) => {
          const stockNo = (item.stockNo || '').toLowerCase();
          const name = (item.name || '').toLowerCase();
          return stockNo.includes(query) || name.includes(query);
        });

        if (catMatches) {
          return cat;
        }
        if (matchingItems.length > 0) {
          return { ...cat, items: matchingItems };
        }
        return null;
      })
      .filter(Boolean);
  }, [categories, filter]);

  const selectedCount = selectedKeys.size;

  const getCategorySelectionState = useCallback(
    (cat) => {
      if (cat.items.length === 0) {
        return { checked: false, indeterminate: false };
      }
      const selectedInCategory = cat.items.filter((item) =>
        selectedKeys.has(itemSelectionKey(cat.code, item.stockNo))
      ).length;
      return {
        checked: selectedInCategory === cat.items.length,
        indeterminate: selectedInCategory > 0 && selectedInCategory < cat.items.length,
      };
    },
    [selectedKeys]
  );

  const toggleCategory = (cat, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const item of cat.items) {
        const key = itemSelectionKey(cat.code, item.stockNo);
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const toggleItem = (catCode, stockNo, checked) => {
    const key = itemSelectionKey(catCode, stockNo);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleExpanded = (catCode) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catCode)) {
        next.delete(catCode);
      } else {
        next.add(catCode);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (submittedRef.current) {
      return;
    }
    if (selectedKeys.size === 0) {
      setModalError('Select at least one category or hire item');
      return;
    }

    const selections = [];
    for (const key of selectedKeys) {
      const separatorIndex = key.indexOf('|');
      selections.push({
        catCode: key.slice(0, separatorIndex),
        stockNo: key.slice(separatorIndex + 1),
      });
    }

    setModalError('');
    setRateForm(emptyRateForm());
    setShowRatesModal(true);
  };

  const handleRatesBack = () => {
    submittedRef.current = false;
    setShowRatesModal(false);
  };

  const handleRatesConfirm = () => {
    if (busy) {
      return;
    }

    const selections = [];
    for (const key of selectedKeys) {
      const separatorIndex = key.indexOf('|');
      selections.push({
        catCode: key.slice(0, separatorIndex),
        stockNo: key.slice(separatorIndex + 1),
      });
    }

    onCreated(agreementName, selections, parseRateForm(rateForm));
  };

  const pendingCount = selectedKeys.size;

  return (
    <>
      <Modal
        show={show && !showRatesModal}
        onHide={onClose}
        onExited={handleModalExited}
        size="xl"
        centered
        enforceFocus={false}
        restoreFocus={false}
        style={{ zIndex: 1060 }}
        dialogClassName="hire-rate-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Quick Create Hire Rates</Modal.Title>
        </Modal.Header>
        <Form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Modal.Body>
            {modalError && <div className="alert alert-danger">{modalError}</div>}
            {treeError && <div className="alert alert-danger">{treeError}</div>}

            <AgreementNameField
              active={show}
              refreshKey="quick-create"
              groupClassName="mb-3 position-relative"
              placeholder="Enter or select agreement name (optional)"
              value={agreementName}
              onChange={setAgreementName}
            />

            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <Form.Control
                type="search"
                placeholder="Filter categories or items..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                disabled={loadingTree}
                style={{ maxWidth: '320px' }}
              />
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                disabled={loadingTree || categories.length === 0}
                onClick={() => setExpandedCategories(new Set(categories.map((cat) => cat.code)))}
              >
                Expand all
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                disabled={loadingTree || categories.length === 0}
                onClick={() => setExpandedCategories(new Set())}
              >
                Collapse all
              </Button>
              <span className="text-muted small ms-auto">{selectedCount} item(s) selected</span>
            </div>

            <div
              className="tree-container border rounded quick-create-tree"
              style={{ maxHeight: '50vh' }}
            >
              {loadingTree ? (
                <div className="p-4 text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading hire items...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="p-4 text-muted text-center">No hire items found</div>
              ) : (
                filteredCategories.map((cat) => {
                  const { checked, indeterminate } = getCategorySelectionState(cat);
                  const isExpanded = expandedCategories.has(cat.code) || filter.trim().length > 0;

                  return (
                    <div key={cat.code} className="tree-node">
                      <div
                        className="tree-node-header expandable"
                        style={{ paddingLeft: 8 }}
                        onClick={() => toggleExpanded(cat.code)}
                      >
                        <span className="tree-toggle">{isExpanded ? '▼' : '▶'}</span>
                        <IndeterminateCheckbox
                          checked={checked}
                          indeterminate={indeterminate}
                          onChange={(e) => toggleCategory(cat, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select all items in ${cat.name}`}
                          className="me-2 mb-0"
                        />
                        <span className="tree-letter-icon" style={{ backgroundColor: '#8b5cf6' }}>
                          C
                        </span>
                        <span className="tree-code">{cat.code}</span>
                        <span className="tree-name">{cat.name}</span>
                        <span className="tree-count">({cat.items.length})</span>
                      </div>
                      {isExpanded && (
                        <div className="tree-children">
                          {cat.items.length === 0 ? (
                            <div className="text-muted small py-2 ps-5">
                              No items in this category
                            </div>
                          ) : (
                            cat.items.map((item) => {
                              const key = itemSelectionKey(cat.code, item.stockNo);
                              return (
                                <div
                                  key={key}
                                  className="tree-node-header"
                                  style={{ paddingLeft: 32 }}
                                  onClick={() =>
                                    toggleItem(cat.code, item.stockNo, !selectedKeys.has(key))
                                  }
                                >
                                  <span className="tree-toggle-placeholder" />
                                  <IndeterminateCheckbox
                                    checked={selectedKeys.has(key)}
                                    indeterminate={false}
                                    onChange={(e) =>
                                      toggleItem(cat.code, item.stockNo, e.target.checked)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Select ${item.stockNo}`}
                                    className="me-2 mb-0"
                                  />
                                  <span
                                    className="tree-letter-icon"
                                    style={{ backgroundColor: '#06b6d4' }}
                                  >
                                    S
                                  </span>
                                  <span className="tree-code">{item.stockNo}</span>
                                  <span className="tree-name">{item.name}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loadingTree || selectedCount === 0 || busy}
            >
              Continue
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={show && showRatesModal}
        onHide={busy ? () => {} : handleRatesBack}
        onExited={handleModalExited}
        size="xl"
        centered
        enforceFocus={false}
        restoreFocus={false}
        backdropClassName="quick-create-rates-backdrop"
        style={{ zIndex: 1070 }}
        dialogClassName="hire-rate-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Set Rates for {pendingCount} item{pendingCount === 1 ? '' : 's'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Values entered here will be applied to all {pendingCount} selected hire rate
            {pendingCount === 1 ? '' : 's'}.
          </p>
          <Row className="g-2">
            {RATE_COLUMNS.map((col) => (
              <Col xl={2} lg={3} md={4} sm={6} key={col.field}>
                <Form.Group className={`mb-2 ${hireRateFieldGroupClass(col.field)}`}>
                  <Form.Label className="small mb-1">{col.label}</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    name={col.field}
                    step={col.step}
                    min={col.min}
                    value={rateForm[col.field]}
                    onChange={(e) => setRateForm((p) => ({ ...p, [col.field]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
          <Form.Group className="mb-0 mt-2">
            <Form.Label>Special Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="special_instructions"
              maxLength={2000}
              value={rateForm.special_instructions}
              onChange={(e) => setRateForm((p) => ({ ...p, special_instructions: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRatesBack} disabled={busy}>
            Back
          </Button>
          <Button variant="primary" onClick={handleRatesConfirm} disabled={busy}>
            {busy ? 'Creating...' : `Create ${pendingCount} rate${pendingCount === 1 ? '' : 's'}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function RecordModal({ record, categories, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(emptyForm());
  const [modalError, setModalError] = useState('');
  const [stockItems, setStockItems] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [showStockMatches, setShowStockMatches] = useState(false);

  useEffect(() => {
    if (!formData.cat) {
      setStockItems([]);
      return undefined;
    }

    let cancelled = false;

    const fetchStockItems = async () => {
      try {
        const response = await axios.get(
          `/api/inclusions/category-items/${encodeURIComponent(formData.cat)}`
        );
        if (!cancelled) {
          setStockItems(response.data || []);
        }
      } catch {
        if (!cancelled) {
          setStockItems([]);
        }
      }
    };

    fetchStockItems();
    return () => {
      cancelled = true;
    };
  }, [formData.cat]);

  useEffect(() => {
    const nextForm = toForm(record);
    setFormData(nextForm);
    setModalError('');
    setShowStockMatches(false);
    setStockSearch('');
  }, [record]);

  useEffect(() => {
    if (!formData.rate_name || stockItems.length === 0) {
      return;
    }
    const match = stockItems.find((item) => item.stock_no === formData.rate_name);
    setStockSearch(match?.name || formData.rate_name);
  }, [formData.rate_name, stockItems]);

  const filteredStockItems = useMemo(() => {
    const query = stockSearch.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return stockItems
      .filter((item) => {
        const name = (item.name || '').toLowerCase();
        const stockNo = (item.stock_no || '').toLowerCase();
        return name.includes(query) || stockNo.includes(query);
      })
      .slice(0, 20);
  }, [stockItems, stockSearch]);

  const handleCategoryChange = (cat) => {
    setFormData((prev) => ({ ...prev, cat, rate_name: '' }));
    setStockSearch('');
    setShowStockMatches(false);
    setModalError('');
  };

  const handleSelectStock = (item) => {
    setFormData((prev) => ({
      ...prev,
      rate_name: item.stock_no || '',
    }));
    setStockSearch(item.name || item.stock_no || '');
    setShowStockMatches(false);
    setModalError('');
  };

  const handleStockSearchChange = (value) => {
    setStockSearch(value);
    setShowStockMatches(true);
    setFormData((prev) => ({
      ...prev,
      rate_name: value,
    }));
    setModalError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cat) {
      setModalError('Please select a category');
      return;
    }

    const rateName = formData.rate_name?.trim() || stockSearch.trim() || null;

    try {
      await onSave(parsePayload({ ...formData, rate_name: rateName || '' }));
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save record');
    }
  };

  return (
    <Modal
      show
      onHide={onClose}
      onExited={cleanupModalBody}
      size="xl"
      centered
      enforceFocus={false}
      restoreFocus={false}
      style={{ zIndex: 1060 }}
      dialogClassName="hire-rate-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{record ? 'Edit Hire Rate' : 'Add Hire Rate'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <Row className="g-3 mb-1">
            <Col lg={4}>
              <AgreementNameField
                inputName="name"
                active
                refreshKey={record?.id ?? 'new'}
                groupClassName="mb-0 position-relative"
                placeholder="Enter or select agreement name (optional)"
                value={formData.name}
                onChange={(name) => setFormData((p) => ({ ...p, name }))}
              />
            </Col>
            <Col lg={4}>
              <Form.Group className="mb-0">
                <Form.Label>Cat *</Form.Label>
                <Form.Select
                  name="cat"
                  value={formData.cat}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  required
                >
                  <option value="">Select category...</option>
                  {categories.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={4}>
              <Form.Group className="mb-0 position-relative">
                <Form.Label>Rate Name / Stock Number</Form.Label>
                <Form.Control
                  type="text"
                  name="rate_name"
                  placeholder={
                    formData.cat
                      ? 'Enter rate name or stock number, or pick from suggestions...'
                      : 'Select a category first'
                  }
                  value={stockSearch}
                  onChange={(e) => handleStockSearchChange(e.target.value)}
                  onFocus={() => setShowStockMatches(true)}
                  onBlur={() => {
                    window.setTimeout(() => setShowStockMatches(false), 150);
                  }}
                  disabled={!formData.cat}
                  autoComplete="off"
                />
                {formData.rate_name && formData.rate_name !== stockSearch && (
                  <Form.Text className="text-muted">
                    Stock number saved: {formData.rate_name}
                  </Form.Text>
                )}
                {showStockMatches &&
                  formData.cat &&
                  stockSearch.trim() &&
                  filteredStockItems.length > 0 && (
                    <ListGroup
                      className="position-absolute w-100 shadow-sm"
                      style={{ zIndex: 1, maxHeight: '220px', overflowY: 'auto' }}
                    >
                      {filteredStockItems.map((item) => (
                        <ListGroup.Item
                          key={item.stock_no}
                          action
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSelectStock(item)}
                        >
                          <div className="fw-medium">{item.name}</div>
                          <div className="small text-muted">Stock: {item.stock_no}</div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                {showStockMatches &&
                  formData.cat &&
                  stockSearch.trim() &&
                  filteredStockItems.length === 0 && (
                    <div className="small text-muted mt-1">
                      No matching stock in this category — your entry will be saved as entered
                    </div>
                  )}
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-3" />

          <Row className="g-2">
            {RATE_COLUMNS.map((col) => (
              <Col xl={2} lg={3} md={4} sm={6} key={col.field}>
                <Form.Group className={`mb-2 ${hireRateFieldGroupClass(col.field)}`}>
                  <Form.Label className="small mb-1">{col.label}</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    name={col.field}
                    step={col.step}
                    min={col.min}
                    value={formData[col.field]}
                    onChange={(e) => setFormData((p) => ({ ...p, [col.field]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>

          <Form.Group className="mb-0 mt-2">
            <Form.Label>Special Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="special_instructions"
              maxLength={2000}
              value={formData.special_instructions}
              onChange={(e) => setFormData((p) => ({ ...p, special_instructions: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function EditableRateCell({
  row,
  field,
  editingCell,
  editingValue,
  savingCell,
  onStartEdit,
  onChange,
  onSave,
  onKeyDown,
}) {
  const cellKey = `${row.id}-${field}`;
  const isEditing = editingCell === cellKey;
  const isSaving = savingCell === cellKey;

  if (isEditing) {
    return (
      <Form.Control
        type="number"
        size="sm"
        step="0.01"
        min="0"
        value={editingValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onSave(row, field)}
        onKeyDown={(e) => onKeyDown(e, row, field)}
        autoFocus
        disabled={isSaving}
        style={{ minWidth: '80px' }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer', display: 'inline-block', minWidth: '48px' }}
      onClick={() => onStartEdit(row, field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(row, field);
        }
      }}
      title="Click to edit"
    >
      {row[field] ?? '—'}
    </span>
  );
}

function PricingAgreementHireRates() {
  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockNumbers, setStockNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [quickCreating, setQuickCreating] = useState(false);
  const [quickCreateProgress, setQuickCreateProgress] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);

  const categoryNameByCode = useMemo(
    () => buildLookupMap(categories, 'code', 'name'),
    [categories]
  );
  const stockNameByNo = useMemo(
    () => buildLookupMap(stockNumbers, 'stock_no', 'name'),
    [stockNumbers]
  );

  const fetchRecords = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const [listRes, categoriesRes, stockNumbersRes] = await Promise.all([
        axios.get('/api/pricing-agreement-hire-rates', { params: { page: 1, limit: 10000 } }),
        axios.get('/api/hire-categories/codes'),
        axios.get('/api/hire-stock/stock-numbers'),
      ]);
      setRecords(asArray(listRes.data?.pricingAgreementHireRates));
      setCategories(asArray(categoriesRes.data));
      setStockNumbers(asArray(stockNumbersRes.data));
    } catch {
      setError('Failed to fetch records');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRecords();
    return () => scheduleModalCleanup();
  }, []);

  const closeRecordModal = () => {
    if (saving) {
      return;
    }
    setShowModal(false);
    setEditing(null);
    scheduleModalCleanup();
  };

  const closeQuickCreateModal = () => {
    if (quickCreating) {
      return;
    }
    setShowQuickCreate(false);
    scheduleModalCleanup();
  };

  const startRateEdit = (row, field) => {
    setEditingCell(`${row.id}-${field}`);
    setEditingValue(row[field] == null ? '' : String(row[field]));
  };

  const cancelRateEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveRateEdit = async (row, field) => {
    const cellKey = `${row.id}-${field}`;
    if (editingCell !== cellKey) {
      return;
    }

    const raw = editingValue.trim();
    let parsed = null;
    if (raw !== '') {
      parsed = parseFloat(raw);
      if (Number.isNaN(parsed) || parsed < 0) {
        setError(`Invalid ${field === 'week_rate' ? 'week' : 'day'} rate`);
        cancelRateEdit();
        return;
      }
    }

    const current = row[field];
    if (raw === '' && (current == null || current === '')) {
      cancelRateEdit();
      return;
    }
    if (parsed !== null && current != null && Number(current) === parsed) {
      cancelRateEdit();
      return;
    }

    setSavingCell(cellKey);
    try {
      const payload = parsePayload({
        ...toForm(row),
        [field]: raw,
      });
      const response = await axios.put(`/api/pricing-agreement-hire-rates/${row.id}`, payload);
      setRecords((prev) =>
        prev.map((record) => (record.id === row.id ? { ...record, ...response.data } : record))
      );
      setSuccess(`${field === 'week_rate' ? 'Week' : 'Day'} rate updated`);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update rate');
    } finally {
      setSavingCell(null);
      cancelRateEdit();
    }
  };

  const handleRateKeyDown = (e, row, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveRateEdit(row, field);
    } else if (e.key === 'Escape') {
      cancelRateEdit();
    }
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/pricing-agreement-hire-rates/${editing.id}`, payload);
        setSuccess('Record updated successfully');
      } else {
        await axios.post('/api/pricing-agreement-hire-rates', payload);
        setSuccess('Record added successfully');
      }
      setShowModal(false);
      setEditing(null);
      await fetchRecords();
      scheduleModalCleanup();
    } finally {
      setSaving(false);
    }
  };

  const handleQuickCreate = (agreementName, selections, rateValues = {}) => {
    setQuickCreating(true);
    setError('');

    (async () => {
      try {
        let created = 0;
        const failures = [];

        initBatchProgress(setQuickCreateProgress, {
          title: 'Creating hire rates...',
          total: selections.length,
        });

        for (const { catCode, stockNo } of selections) {
          try {
            await axios.post(
              '/api/pricing-agreement-hire-rates',
              buildQuickCreatePayload(agreementName, catCode, stockNo, rateValues)
            );
            created += 1;
            tickBatchProgress(setQuickCreateProgress, { success: true });
          } catch (err) {
            failures.push(
              `${stockNo} (${catCode}): ${err.response?.data?.error || err.message || 'Failed'}`
            );
            tickBatchProgress(setQuickCreateProgress, { failed: true });
          }
        }

        if (created === 0) {
          setError(failures[0] || 'No hire rates were created');
          return;
        }

        setShowQuickCreate(false);
        await fetchRecords({ silent: true });

        if (failures.length > 0) {
          setSuccess(`Created ${created} hire rate(s). ${failures.length} failed.`);
          setError(failures.slice(0, 3).join('; '));
        } else {
          setSuccess(`Created ${created} hire rate${created === 1 ? '' : 's'} successfully`);
          setError('');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to create hire rates');
      } finally {
        setQuickCreating(false);
        clearBatchProgress(setQuickCreateProgress);
        scheduleModalCleanup();
      }
    })();
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete ALL hire rate records? This action cannot be undone!'
      )
    ) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all your pricing agreement hire rate records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/pricing-agreement-hire-rates');
      setSuccess(
        `All hire rates deleted successfully (${response.data.deletedCount || 0} records)`
      );
      setError('');
      await fetchRecords({ silent: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete all hire rates');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-2">
        <Col>
          <h1 className="mb-0">Pricing Agreement Hire Rates</h1>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => {
              setShowQuickCreate(true);
            }}
          >
            Quick Create
          </Button>
          <Button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
          >
            Add Hire Rate
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {quickCreateProgress && (
        <OperationProgress
          title={quickCreateProgress.title}
          processed={quickCreateProgress.processed}
          total={quickCreateProgress.total}
          success={quickCreateProgress.success}
          failed={quickCreateProgress.failed}
          variant="info"
        />
      )}
      <Card>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4">Loading...</div>
          ) : (
            <div className="table-responsive scrollable-grid">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Agreement</th>
                    <th>Cat</th>
                    <th>Rate Name</th>
                    <th>Week</th>
                    <th>Day</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>
                        <LookupCell
                          code={row.cat}
                          label={categoryNameByCode.get(String(row.cat || '').toLowerCase())}
                        />
                      </td>
                      <td>
                        <LookupCell
                          code={row.rate_name}
                          label={
                            row.rate_name
                              ? stockNameByNo.get(String(row.rate_name).toLowerCase())
                              : null
                          }
                        />
                      </td>
                      <td>
                        <EditableRateCell
                          row={row}
                          field="week_rate"
                          editingCell={editingCell}
                          editingValue={editingValue}
                          savingCell={savingCell}
                          onStartEdit={startRateEdit}
                          onChange={setEditingValue}
                          onSave={saveRateEdit}
                          onKeyDown={handleRateKeyDown}
                        />
                      </td>
                      <td>
                        <EditableRateCell
                          row={row}
                          field="day_rate"
                          editingCell={editingCell}
                          editingValue={editingValue}
                          savingCell={savingCell}
                          onStartEdit={startRateEdit}
                          onChange={setEditingValue}
                          onSave={saveRateEdit}
                          onKeyDown={handleRateKeyDown}
                        />
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-1"
                          onClick={() => {
                            setEditing(row);
                            setShowModal(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={async () => {
                            if (!window.confirm('Delete this record?')) return;
                            try {
                              await axios.delete(`/api/pricing-agreement-hire-rates/${row.id}`);
                              fetchRecords();
                            } catch (err) {
                              setError(err.response?.data?.error || 'Failed to delete record');
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Card>
      {showModal && (
        <RecordModal
          record={editing}
          categories={categories}
          saving={saving}
          onClose={closeRecordModal}
          onSave={handleSave}
        />
      )}
      {(showQuickCreate || quickCreating) && (
        <QuickCreateModal
          show={showQuickCreate}
          onClose={closeQuickCreateModal}
          onCreated={handleQuickCreate}
          busy={quickCreating}
        />
      )}
    </Container>
  );
}

export default PricingAgreementHireRates;
